/**
 * Single source of truth for the site's public origin (canonical domain).
 *
 * Server reads `APP_BASE_URL` (Zod-validated in `server/env.ts`).
 * Client reads `VITE_APP_BASE_URL` (Vite `import.meta.env`; mirrored from
 * `APP_BASE_URL` at build time in `vite.config.ts` when unset).
 *
 * Default / fallback is verascap.com — the only public site of record.
 * Override per environment via APP_BASE_URL; do not hardcode domains into
 * call sites. Legacy eb-5visa.com hosts redirect separately (never emit
 * them in sitemap / robots / canonicals / OG / JSON-LD / llms).
 */

export const DEFAULT_SITE_DOMAIN = "verascap.com";
export const DEFAULT_SITE_URL = `https://${DEFAULT_SITE_DOMAIN}`;

export type SiteUrl = {
  /** Origin with scheme, no trailing slash — e.g. `https://verascap.com` */
  domainUrl: string;
  /** Hostname only — e.g. `verascap.com` */
  domain: string;
};

/**
 * Normalize an optional absolute URL (or hostname) into `{ domainUrl, domain }`.
 * Invalid / empty input falls back to {@link DEFAULT_SITE_URL}.
 */
export function resolveSiteUrl(raw?: string | null): SiteUrl {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) {
    return { domainUrl: DEFAULT_SITE_URL, domain: DEFAULT_SITE_DOMAIN };
  }

  // Reject non-http(s) schemes before we prepend https:// (which would turn
  // `ftp://host` into the nonsense URL `https://ftp://host`).
  const schemeMatch = /^([a-z][a-z0-9+.-]*):\/\//i.exec(trimmed);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme !== "http" && scheme !== "https") {
      return { domainUrl: DEFAULT_SITE_URL, domain: DEFAULT_SITE_DOMAIN };
    }
  }

  const withScheme = schemeMatch ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(withScheme);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return { domainUrl: DEFAULT_SITE_URL, domain: DEFAULT_SITE_DOMAIN };
    }
    let domain = url.hostname.toLowerCase();
    if (!domain) {
      return { domainUrl: DEFAULT_SITE_URL, domain: DEFAULT_SITE_DOMAIN };
    }
    // Canonical public host is always apex. `www.` is a redirect source only
    // (see shared/canonical-host-redirect.ts). If APP_BASE_URL is set to a
    // www origin, strip it so www→apex consolidation can fire.
    if (domain.startsWith("www.")) {
      domain = domain.slice(4);
    }
    if (!domain) {
      return { domainUrl: DEFAULT_SITE_URL, domain: DEFAULT_SITE_DOMAIN };
    }
    return {
      domainUrl: `${url.protocol}//${domain}`,
      domain,
    };
  } catch {
    return { domainUrl: DEFAULT_SITE_URL, domain: DEFAULT_SITE_DOMAIN };
  }
}
